"use client";

import { useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import {
  validatePassword,
  getPasswordRequirements,
  type PasswordRequirement,
} from "@/lib/validation/password";

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  showRequirements?: boolean;
  className?: string;
}

export function PasswordInput({
  value,
  onChange,
  label = "Password",
  placeholder = "Enter your password",
  required = false,
  disabled = false,
  showRequirements = true,
  className = "",
}: PasswordInputProps) {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validation = validatePassword(value);
  const requirements = getPasswordRequirements(value);
  const shouldShowFeedback = hasInteracted && value.length > 0;

  const handleChange = (newValue: string) => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    onChange(newValue);
  };

  return (
    <div className="space-y-2">
      <label className="flex flex-col gap-2 text-sm text-neutral-700">
        {label}
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
          <input
            type={showPassword ? "text" : "password"}
            required={required}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={() => setHasInteracted(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full rounded-md border ${
              shouldShowFeedback && !validation.isValid
                ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                : "border-neutral-200 focus:border-primary focus:ring-primary/20"
            } bg-white pl-11 pr-20 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:outline-none focus:ring-2 transition-colors ${className}`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-neutral-900 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-primary/20"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
      </label>

      {shouldShowFeedback && !validation.isValid && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {validation.errors[0]}
        </div>
      )}

      {showRequirements && (
        <div className="space-y-1.5 pt-1">
          {requirements.map((req, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 text-xs ${
                req.met ? "text-green-700" : "text-neutral-500"
              }`}
            >
              <span className={req.met ? "text-green-500" : "text-neutral-500"}>
                {req.met ? "✓" : "○"}
              </span>
              <span>{req.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

